/**
 * Art manifest — the single source of truth for every loaded image asset.
 *
 * Pipeline: hand-authored SVGs live under `public/assets/` (served at the site
 * root by Vite, precached offline by the PWA's SVG glob). At load time
 * PreloadScene rasterises each SVG into a Phaser texture at `raster` px via
 * `this.load.svg(key, path, { width, height })`. The renderer then looks up a
 * texture by `key` and falls back to its old vector primitive if the key is
 * missing — so the game never hard-depends on an asset existing.
 *
 * Convention: combat-entity tokens are keyed `unit_<id>` / `enemy_<id>` so the
 * renderer can resolve them straight from a unit/enemy `defId` with no extra
 * mapping. A token is a circular medallion that doubles as the battle sprite
 * AND the roster/HUD icon — authored once, used everywhere.
 */

export interface AssetDef {
  /** Texture key used by `this.textures` / `this.add.image(key)`. */
  key: string;
  /** URL under the site root (file lives at `public/<path>`). */
  path: string;
  /** Rasterisation size in px (square unless `h` given). ~2× display for crisp zoom.
   *  Ignored when `image` is set (raster bitmaps load at their native resolution). */
  raster: number;
  /** Optional non-square raster height (e.g. the world backdrop). */
  rasterH?: number;
  /** Load as a raster bitmap (PNG/JPG) via `load.image` instead of rasterising an
   *  SVG. Used for the AI-painted Olympus backdrop, which has no vector source. */
  image?: boolean;
  /** Authored out-of-band (e.g. an AI raster) and may not be committed yet. The
   *  manifest still validates its shape, but the on-disk file check skips it so an
   *  un-generated asset doesn't red-build. Remove once the file lands in `public/`. */
  pending?: boolean;
}

const token = (key: string, path: string): AssetDef => ({ key, path, raster: 128 });
/** A side-view building token rasterised at ~2× its SVG viewBox so the aspect is
 *  preserved (units are now structures, not square medallions). */
const building = (key: string, path: string, w: number, h: number): AssetDef => ({
  key,
  path,
  raster: w,
  rasterH: h,
});

/** Unit roster tokens — `unit_<id>` matches a unit `defId`. Each is a side-view
 *  BUILDING (watchtower, barracks, spire, shrine…) rasterised to its SVG aspect.
 *  Branch tokens are keyed `unit_<id>_<branchId>` and swap in once the player
 *  picks a specialization (see BattleScene.unitTokenKey); the base token is the
 *  fallback if a branch has no art yet. */
export const UNIT_ASSETS: AssetDef[] = [
  building('unit_footmen', 'assets/units/footmen.svg', 144, 104),
  building('unit_archers', 'assets/units/archers.svg', 112, 192),
  building('unit_frost_spire', 'assets/units/frost_spire.svg', 112, 192),
  building('unit_mage', 'assets/units/mage.svg', 112, 200),
  building('unit_pilgrim_warden', 'assets/units/pilgrim_warden.svg', 152, 144),
  // Specialization branch tokens — the visible payoff of upgrading.
  building('unit_footmen_shieldwall', 'assets/units/shieldwall.svg', 144, 112),
  building('unit_footmen_reaver', 'assets/units/reaver.svg', 144, 104),
  building('unit_archers_marksman', 'assets/units/crossbows.svg', 120, 208),
  building('unit_archers_skirmisher', 'assets/units/skirmisher.svg', 120, 168),
];

/** Enemy tokens — `enemy_<id>` matches an enemy `defId`. */
export const ENEMY_ASSETS: AssetDef[] = [
  token('enemy_shade', 'assets/enemies/shade.svg'),
  token('enemy_fury_swarmling', 'assets/enemies/fury_swarmling.svg'),
  token('enemy_bronze_brute', 'assets/enemies/bronze_brute.svg'),
  token('enemy_harpy', 'assets/enemies/harpy.svg'),
  token('enemy_unmade_ravager', 'assets/enemies/unmade_ravager.svg'),
  token('enemy_unmade_colossus', 'assets/enemies/unmade_colossus.svg'),
  token('enemy_bronze_myrmidon', 'assets/enemies/bronze_myrmidon.svg'),
  token('enemy_bronze_shard', 'assets/enemies/bronze_shard.svg'),
  token('enemy_talos_sentinel', 'assets/enemies/talos_sentinel.svg'),
  token('enemy_molten_myrmidon', 'assets/enemies/molten_myrmidon.svg'),
  token('enemy_gilded_eagle', 'assets/enemies/gilded_eagle.svg'),
  token('enemy_forge_warden', 'assets/enemies/forge_warden.svg'),
];

/** Combat FX. Projectiles point right at 0°; the renderer rotates toward target. */
export const FX_ASSETS: AssetDef[] = [
  { key: 'fx_arrow', path: 'assets/fx/arrow.svg', raster: 64 },
  { key: 'fx_bolt', path: 'assets/fx/bolt.svg', raster: 64 },
  { key: 'fx_mote', path: 'assets/fx/mote.svg', raster: 48 },
  { key: 'fx_impact', path: 'assets/fx/impact.svg', raster: 96 },
  { key: 'fx_shatter', path: 'assets/fx/shatter.svg', raster: 96 },
  { key: 'fx_rift_flash', path: 'assets/fx/rift_flash.svg', raster: 96 },
  { key: 'fx_frost', path: 'assets/fx/frost.svg', raster: 64 },
];

/** Stage dressing for the Olympus biome. */
export const WORLD_ASSETS: AssetDef[] = [
  // AI-painted Olympus backdrop (Gemini). Preferred when present; drops in as a
  // raster bitmap at the world's portrait aspect. See art/olympus_background.prompt.md.
  { key: 'world_background', path: 'assets/world/background.jpg', raster: 1280, rasterH: 2200, image: true },
  // Hand-authored SVG backdrop — the live fallback until the PNG is generated,
  // and the offline/lo-fi option if we ever revert. Resolved by BattleScene after
  // `world_background`. See [[art-pipeline]].
  { key: 'world_background_svg', path: 'assets/world/background.svg', raster: 720, rasterH: 1238 },
  { key: 'world_spawn', path: 'assets/world/spawn_portal.svg', raster: 256 },
  { key: 'world_gate', path: 'assets/world/gate.svg', raster: 256 },
  // Scatter props — frame the lane and mark the ground you defend.
  { key: 'world_column', path: 'assets/world/column.svg', raster: 72, rasterH: 180 },
  { key: 'world_column_broken', path: 'assets/world/column_broken.svg', raster: 160, rasterH: 120 },
  { key: 'world_brazier', path: 'assets/world/brazier.svg', raster: 120, rasterH: 180 },
  { key: 'world_rubble', path: 'assets/world/rubble.svg', raster: 168, rasterH: 98 },
  { key: 'world_statue', path: 'assets/world/statue.svg', raster: 84, rasterH: 192 },
];

/** HUD / menu chrome. */
export const UI_ASSETS: AssetDef[] = [
  { key: 'ui_heart', path: 'assets/ui/heart.svg', raster: 64 },
  { key: 'ui_coin', path: 'assets/ui/coin.svg', raster: 64 },
  { key: 'ui_star_full', path: 'assets/ui/star_full.svg', raster: 64 },
  { key: 'ui_star_empty', path: 'assets/ui/star_empty.svg', raster: 64 },
  { key: 'ui_slot_ground', path: 'assets/ui/slot_ground.svg', raster: 96 },
  { key: 'ui_slot_tower', path: 'assets/ui/slot_tower.svg', raster: 96 },
  { key: 'ui_slot_support', path: 'assets/ui/slot_support.svg', raster: 96 },
  { key: 'ui_emblem', path: 'assets/ui/emblem.svg', raster: 512 },
  // Threat tags for the wave preview.
  { key: 'ui_tag_flying', path: 'assets/ui/tag_flying.svg', raster: 48 },
  { key: 'ui_tag_armored', path: 'assets/ui/tag_armored.svg', raster: 48 },
  { key: 'ui_tag_swarm', path: 'assets/ui/tag_swarm.svg', raster: 48 },
  { key: 'ui_menu_banner', path: 'assets/ui/menu_banner.svg', raster: 720, rasterH: 460 },
  // World-map (level-select) atlas. AI-painted plate (Gemini) preferred when
  // present; the hand-authored SVG is the live fallback until it lands. See
  // art/world_map.prompt.md. Resolved by WorldMapScene (jpg → svg → gradient).
  { key: 'world_map', path: 'assets/ui/world_map.jpg', raster: 1440, rasterH: 2912, image: true },
  { key: 'world_map_svg', path: 'assets/ui/world_map.svg', raster: 1440, rasterH: 2912 },
];

/** Everything the loader should pull, in load order. */
export const ALL_ASSETS: AssetDef[] = [
  ...WORLD_ASSETS,
  ...UNIT_ASSETS,
  ...ENEMY_ASSETS,
  ...FX_ASSETS,
  ...UI_ASSETS,
];
